import { Stack, useTheme } from "@mui/material";
import { Banner, EvolutionChart } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { Loading } from "src/components/utils/Loading";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { getScoreDetailRemoteState } from "src/context/ScoreDetail/scoreDetailRemoteState";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";
import type { ComponentAppearance } from "src/components/appearance";

const LegacyEvolutionChart = () => {
    const values = [
        { label: "Rubrique 1", data: [10, 20] },
        { label: "Rubrique 2", data: [20, 30] },
    ];
    const labels: string[] = ["2022", "2024"];

    return (
        <Stack alignItems={"center"} justifyContent={"center"}>
            <EvolutionChart
                height="500px"
                width="500px"
                values={values}
                labels={labels}
                legend={{
                    position: "right",
                }}
            />
        </Stack>
    );
};

type EvolutionChart2Props = { appearance?: ComponentAppearance };

export const EvolutionChart2 = ({ appearance = "legacy" }: EvolutionChart2Props) => {
    const isProductUxV2 = appearance === "product-v2";
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        score,
        olapScores,
        olapPrevScores,
        isLoading,
        scoreQueryState,
        scoreQueryError,
        retryScoreQuery,
        prevCampaignLabel,
    } = useScoreDetailContext();

    if (!isProductUxV2) return <LegacyEvolutionChart />;

    const resolvedScoreQueryState =
        scoreQueryState ??
        getScoreDetailRemoteState({
            score: olapScores,
            isLoading: isLoading ?? false,
            error: scoreQueryError,
        });

    if (resolvedScoreQueryState.status === "loading") return <Loading appearance={appearance} />;

    if (
        resolvedScoreQueryState.status === "contract-error" ||
        resolvedScoreQueryState.status === "forbidden" ||
        resolvedScoreQueryState.status === "not-found" ||
        resolvedScoreQueryState.status === "server-error"
    ) {
        return <ScoreQueryError context="details" onRetry={retryScoreQuery} status={resolvedScoreQueryState.status} />;
    }

    const currentValue = OlapScoresService.normalizeNumericalScore(olapScores?.score.value);
    const previousValue = OlapScoresService.normalizeNumericalScore(olapPrevScores?.score.value);

    if (currentValue === null) {
        return <Banner variant="info">{t("product.score_details.score_unavailable", "Score unavailable")}</Banner>;
    }

    if (previousValue === null) {
        return (
            <Banner variant="info">
                {t("product.score_details.evolution.no_previous_campaign", "No previous campaign score available")}
            </Banner>
        );
    }

    return (
        <EvolutionChart
            height={theme.spacing(50)}
            width="100%"
            labels={[
                prevCampaignLabel || t("product.score_details.evolution.previous_campaign", "Previous campaign"),
                t("product.score_details.evolution.current_campaign", "Current campaign"),
            ]}
            values={[{ label: score.title, data: [previousValue, currentValue] }]}
            unit={olapScores?.unit}
            precision={score.precision}
            improvement={score.improvement}
            legend={{ position: "bottom" }}
        />
    );
};
